<template>
  <div class="flex flex-wrap justify-between items-center gap-y-8">
    <div class="flex gap-4 flex-wrap">
      <BittsQuickFilter
        v-for="filter in filters"
        v-model="filterValues[filter.value]"
        :key="filter.value"
        :filter="filter"
        :open-on-mount="false"
        :show-search="filter.useSearch ?? false"
        :show-header="false"
        :no-custom-value="true"
        :disabled="isActionBarDisabled"
        size="medium"
        :options="filter.options"
        :use-select-all="shouldUseSelectAll(filter.value)"
        @clear-filter="handleClearFilter(filter.value)"
        @apply-filter="handleApplyFilter"
        @remove-filter="handleRemoveFilter(filter.value)"
      />
    </div>
    <div class="flex gap-6">
      <BittsInput
        v-model="searchValue"
        name="search"
        :placeholder="searchPlaceholder"
        type="search"
        size="small"
        :disabled="isActionBarDisabled"
        @update:model-value="handleSearch"
      />
      <BittsButton
        v-if="showEditButton"
        class="text-14"
        text="Bulk Edit"
        size="small"
        :disabled="isActionBarDisabled"
        @click="emit('edit-clicked')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsInput, BittsQuickFilter } from '@crossbeam/bitts';
import { BittsIcon } from '@crossbeam/bitts/src/types';
import { EVENT_SITES } from '@crossbeam/itly';

import { useDebounceFn } from '@vueuse/core';
import { computed, onMounted, reactive, ref } from 'vue';

import useIteratively from '@/composables/useIteratively';
import {
  CROSSBEAM_DEFAULT_PRESET_TYPE,
  NEW_SHARING_TYPES_MAP,
} from '@/constants/data_shares';
import { useDataSharePresetsStore } from '@/stores';

type FilterOption = {
  label: string;
  value: string | number;
  hidden?: boolean;
};

type Filter = {
  value: string;
  label: string;
  faIcon: BittsIcon;
  options: FilterOption[];
  datatype?: 'datetime' | 'number' | 'lookup' | 'text' | 'radio';
  useSearch?: boolean;
  preserveOrder?: boolean;
};

type FilterValues = Record<string, (string | number)[]>;

type Props = {
  isActionBarDisabled?: boolean;
  searchPlaceholder?: string;
  firstFilter: Filter;
  initialFilterKeys: string[];
  showEditButton?: boolean;
  includeSharedFieldsFilter?: boolean;
  includeOverridesFilter?: boolean;
  initialFilterValues?: FilterValues;
  feedId?: number;
  sourceId?: number;
  /** Event site for dataSharingAction (e.g. default sharing, my data, partner populations). */
  eventSite?: string;
};

const props = withDefaults(defineProps<Props>(), {
  isActionBarDisabled: false,
  searchPlaceholder: 'Search',
  showEditButton: true,
  includeSharedFieldsFilter: true,
  includeOverridesFilter: false,
  initialFilterValues: undefined,
  feedId: undefined,
  sourceId: undefined,
  eventSite: undefined,
});

const emit = defineEmits<{
  'search-applied': [value: string];
  'filters-applied': [filterValues: FilterValues];
  'edit-clicked': [];
}>();

const dataSharePresetsStore = useDataSharePresetsStore();
const { iteratively } = useIteratively();

const presetOptions = computed<FilterOption[]>(() => {
  // If feedId is provided, filter presets by data source
  const presetsToUse =
    props.feedId !== undefined
      ? dataSharePresetsStore.getFilteredPresetsByFeeId(
          props.feedId,
          props.sourceId,
        )
      : dataSharePresetsStore.allPresets;

  const namedPresets = presetsToUse.filter(
    (preset) => preset.preset_type === 'named_preset' && preset.name,
  );

  // Use a Set to track unique preset names
  const uniqueNames = new Set<string>();
  const options: FilterOption[] = [];

  namedPresets.forEach((preset) => {
    if (preset.name && !uniqueNames.has(preset.name)) {
      uniqueNames.add(preset.name);
      options.push({
        label: preset.name,
        value: preset.name,
      });
    }
  });

  // Add single crossbeam_default option if any exist
  const hasCrossbeamDefault = presetsToUse.some(
    (preset) => preset.preset_type === 'crossbeam_default',
  );

  if (hasCrossbeamDefault) {
    options.unshift({
      label: dataSharePresetsStore.crossbeamPresetLabel,
      value: CROSSBEAM_DEFAULT_PRESET_TYPE,
    });
  }

  return options;
});

const filters = computed<Filter[]>(() => {
  const baseFilters: Filter[] = [
    props.firstFilter,
    {
      value: 'sharing_level',
      options: Object.values(NEW_SHARING_TYPES_MAP).map((option) => ({
        label: option.label,
        value: option.value,
      })),
      label: 'Sharing Level',
      faIcon: ['fak', 'settings'] as BittsIcon,
      preserveOrder: true,
    },
  ];

  if (props.includeSharedFieldsFilter) {
    baseFilters.push({
      value: 'field_presets',
      options: presetOptions.value,
      label: 'Field Presets',
      faIcon: ['fak', 'slider'] as BittsIcon,
      useSearch: true,
    });
  }

  if (props.includeOverridesFilter) {
    baseFilters.push({
      value: 'overrides',
      options: [
        { label: 'All', value: 'all', hidden: true },
        { label: 'True', value: 'true' },
        { label: 'False', value: 'false' },
      ],
      label: 'Overrides',
      faIcon: ['fak', 'override'] as BittsIcon,
      datatype: 'radio',
      useSearch: false,
    });
  }

  return baseFilters;
});

const filterValues = reactive<FilterValues>(
  props.initialFilterKeys.reduce((acc, key) => {
    acc[key] = props.initialFilterValues?.[key] ?? [];
    return acc;
  }, {} as FilterValues),
);

const searchValue = ref('');

onMounted(() => {
  if (props.initialFilterValues) {
    emit('filters-applied', { ...filterValues });
  }
});

const dataSharingEventSite = computed(
  () => props.eventSite ?? EVENT_SITES.DATA_SHARING_POPULATION_TAB_ACTIONS,
);

const debouncedSearch = useDebounceFn((value: string) => {
  iteratively.dataSharingAction({
    event_site: dataSharingEventSite.value,
    metadata: { action: 'search', search_term: value },
  });
  emit('search-applied', value);
}, 300);

function handleSearch(value: string) {
  debouncedSearch(value);
}

function trackFiltersAction(action: 'applied_filters' | 'cleared_filters') {
  iteratively.dataSharingAction({
    event_site: dataSharingEventSite.value,
    metadata: {
      action,
      filter_values: { ...filterValues },
    },
  });
}

function handleClearFilter(filterKey: string) {
  filterValues[filterKey] = [];
  trackFiltersAction('cleared_filters');
  emit('filters-applied', { ...filterValues });
}

function handleRemoveFilter(filterKey: string) {
  filterValues[filterKey] = [];
  trackFiltersAction('cleared_filters');
  emit('filters-applied', { ...filterValues });
}

function handleApplyFilter() {
  trackFiltersAction('applied_filters');
  emit('filters-applied', { ...filterValues });
}

function shouldUseSelectAll(filterValue: string) {
  // Enable select-all for population_type (partner tags) and field_presets (presets)
  return filterValue === 'partner_tag' || filterValue === 'field_presets';
}

function clearAllFilters() {
  // Clear all filter values
  Object.keys(filterValues).forEach((key) => {
    filterValues[key] = [];
  });
  // Clear search value
  searchValue.value = '';
  trackFiltersAction('cleared_filters');
  // Emit the cleared state
  emit('filters-applied', { ...filterValues });
  emit('search-applied', '');
}

defineExpose({
  clearAllFilters,
});
</script>
